import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { AppointmentBookingResource, AppointmentBookingStatus } from '@neuroedge/shared';
import { Button, Card, Modal, PageState, Select, useToast } from '../../components/ui';
import { NewAppointmentModal } from './NewAppointmentModal';
import { useAuth } from '../../auth/AuthContext';
import {
  useAppointments,
  useAvailableSlots,
  useCancelAppointment,
  useCompleteAppointment,
  useRescheduleAppointment,
} from '../../api/appointments';
import { FlowAnswersList } from '../flow-answers/FlowAnswersList';
import {
  CalendarView,
  dayKey,
  startOfMonth,
  visibleRange,
  type CalendarEvent,
} from '../../components/calendar/CalendarView';

const STATUS_OPTIONS: { value: AppointmentBookingStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'BOOKED', label: 'Booked' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'COMPLETED', label: 'Completed' },
];

function formatRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const datePart = start.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const startTime = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const endTime = end.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${datePart}, ${startTime}–${endTime}`;
}

function RescheduleModal({
  appointment,
  onClose,
}: {
  appointment: AppointmentBookingResource;
  onClose: () => void;
}) {
  const toast = useToast();
  const slots = useAvailableSlots(appointment.id, true);
  const reschedule = useRescheduleAppointment(appointment.id);
  const [notify, setNotify] = useState(true);

  async function onPick(startIso: string, label: string) {
    if (!window.confirm(`Reschedule the ${appointment.serviceName} appointment to ${label}?`)) {
      return;
    }
    try {
      await reschedule.mutateAsync({ newStartIso: startIso, notify });
      toast.success(
        notify ? 'Appointment rescheduled — customer notified.' : 'Appointment rescheduled.',
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not reschedule the appointment.');
    }
  }

  return (
    <Modal title={`Reschedule ${appointment.serviceName}`} onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Currently {formatRange(appointment.scheduledStart, appointment.scheduledEnd)}. Pick a new
          time below.
        </p>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
          }}
        >
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          Notify customer
        </label>

        {slots.isPending ? (
          <PageState kind="loading" message="Loading available times…" />
        ) : slots.isError ? (
          <PageState
            kind="error"
            message={slots.error.message}
            onRetry={() => void slots.refetch()}
          />
        ) : slots.data.length === 0 ? (
          <PageState
            kind="empty"
            title="No open slots"
            message="This service has no open slots in its booking window right now."
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gap: 'var(--space-2)',
              maxHeight: 320,
              overflowY: 'auto',
              paddingRight: 'var(--space-1)',
            }}
          >
            {slots.data.map((s) => (
              <Button
                key={s.startIso}
                variant="secondary"
                disabled={reschedule.isPending}
                onClick={() => void onPick(s.startIso, s.label)}
              >
                {s.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function AppointmentRow({
  appointment,
  canManage,
}: {
  appointment: AppointmentBookingResource;
  canManage: boolean;
}) {
  const toast = useToast();
  const cancel = useCancelAppointment();
  const complete = useCompleteAppointment();
  const [notify, setNotify] = useState(true);
  const [rescheduling, setRescheduling] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const answers = Array.isArray(appointment.metadata?.answers)
    ? (appointment.metadata.answers as unknown[])
    : [];
  const hasDetails = Boolean(appointment.notes) || answers.length > 0;

  async function onCancel() {
    if (!window.confirm(`Cancel the ${appointment.serviceName} appointment?`)) return;
    try {
      await cancel.mutateAsync({ id: appointment.id, notify });
      toast.success(
        notify ? 'Appointment cancelled — customer notified.' : 'Appointment cancelled.',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel the appointment.');
    }
  }

  async function onComplete() {
    if (!window.confirm(`Mark the ${appointment.serviceName} appointment as completed?`)) return;
    try {
      await complete.mutateAsync({ id: appointment.id, notify });
      toast.success(
        notify ? 'Appointment completed — customer notified.' : 'Appointment completed.',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not complete the appointment.');
    }
  }

  return (
    <li
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        borderBottom: '1px solid var(--color-border)',
        paddingBottom: 'var(--space-2)',
      }}
    >
      <div
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        <strong>{appointment.serviceName}</strong>
        <span>{formatRange(appointment.scheduledStart, appointment.scheduledEnd)}</span>
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {appointment.contactName ?? 'Unknown contact'}
        </span>
        <span className="ne-badge">{appointment.status}</span>
        {hasDetails ? (
          <Button variant="secondary" onClick={() => setShowDetails((v) => !v)}>
            {showDetails ? 'Hide details' : 'Details'}
          </Button>
        ) : null}
        <span style={{ flex: 1, minWidth: 0 }} />
        {canManage && appointment.status === 'BOOKED' ? (
          <>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              Notify
            </label>
            <Button
              variant="secondary"
              disabled={complete.isPending}
              onClick={() => void onComplete()}
            >
              Complete
            </Button>
            <Button variant="secondary" onClick={() => setRescheduling(true)}>
              Reschedule
            </Button>
            <Button variant="secondary" disabled={cancel.isPending} onClick={() => void onCancel()}>
              Cancel
            </Button>
          </>
        ) : null}
      </div>
      {showDetails ? (
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-1)',
            padding: 'var(--space-2)',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {appointment.notes ? (
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
              <strong>Notes:</strong> {appointment.notes}
            </p>
          ) : null}
          <FlowAnswersList metadata={appointment.metadata} />
        </div>
      ) : null}
      {rescheduling ? (
        <RescheduleModal appointment={appointment} onClose={() => setRescheduling(false)} />
      ) : null}
    </li>
  );
}

function eventTone(a: AppointmentBookingResource): CalendarEvent['tone'] {
  if (a.status === 'COMPLETED') return 'success';
  if (a.status === 'CANCELLED') return 'muted';
  return 'default';
}

/**
 * Bookings the appointment-booking conversation flow creates, as a month
 * calendar (docs/API_SPEC.md section 21): each day shows its bookings
 * (time, service, customer; completed in green, cancelled struck through);
 * picking a day lists them below with cancel / complete / reschedule, each
 * with an optional WhatsApp notification. Bookings themselves are only
 * ever created by the WhatsApp flow, never from this page.
 */
export function AppointmentsPage() {
  const { hasPermission } = useAuth();
  const [status, setStatus] = useState<AppointmentBookingStatus | ''>('');
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(() => new Date());
  const [adding, setAdding] = useState(false);

  const range = visibleRange(month);
  const appointments = useAppointments({
    status: status || undefined,
    from: range.from.toISOString(),
    to: range.to.toISOString(),
    sortOrder: 'asc',
    pageSize: 1000,
  });

  const canManage = hasPermission('products.manage');
  const items = appointments.data?.items ?? [];
  const events: CalendarEvent[] = items.map((a) => ({
    id: a.id,
    start: a.scheduledStart,
    label: `${new Date(a.scheduledStart).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })} ${a.serviceName}${a.contactName ? ` · ${a.contactName}` : ''}`,
    tone: eventTone(a),
  }));
  const selectedKey = dayKey(selected);
  const dayItems = items.filter((a) => dayKey(new Date(a.scheduledStart)) === selectedKey);
  const dayLabel = selected.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Appointments</h1>
        <span style={{ flex: 1 }} />
        {canManage ? (
          <Button type="button" onClick={() => setAdding(true)}>
            + New appointment
          </Button>
        ) : null}
        <Select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as AppointmentBookingStatus | '')}
          options={STATUS_OPTIONS}
        />
      </div>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Bookings customers made through the WhatsApp appointment-booking flow, and ones your team
        added by hand. Set up bookable services on the{' '}
        <Link to="/products-services">Products & Services</Link> page.
      </p>
      {adding ? (
        <NewAppointmentModal initialDate={selected} onClose={() => setAdding(false)} />
      ) : null}

      <Card>
        {appointments.isError ? (
          <PageState
            kind="error"
            message={appointments.error.message}
            onRetry={() => void appointments.refetch()}
          />
        ) : (
          <CalendarView
            month={month}
            onMonthChange={setMonth}
            events={events}
            selectedDay={selectedKey}
            onSelectDay={(d) => {
              setSelected(d);
              if (d.getMonth() !== month.getMonth()) setMonth(startOfMonth(d));
            }}
            loading={appointments.isFetching}
            itemNoun="appointment"
          />
        )}
      </Card>

      <Card title={dayLabel}>
        {appointments.isPending ? (
          <PageState kind="loading" message="Loading appointments…" />
        ) : dayItems.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>No appointments this day.</p>
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {dayItems.map((a) => (
              <AppointmentRow key={a.id} appointment={a} canManage={canManage} />
            ))}
          </ul>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
